import { runInAction } from 'mobx';
import { useLocalObservable } from 'mobx-react-lite';
import { createContext } from 'react';

/**
 * The per-session edited-goal-IDs set. `add` marks a goal's URL as edited this
 * session; `has` checks it. A plain `Set` and MobX's `ObservableSet` both satisfy
 * this, and the validation gate uses only `has`.
 */
export interface IEditedGoalIds {
  /** Mark a goal's URL as edited this session. */
  add: (goalId: string) => void;
  /** Has this goal's URL been edited this session? */
  has: (goalId: string) => boolean;
}

/**
 * Per-session set of URL Visit goal IDs whose URL was edited. AdWizard supplies
 * an observable set; the default is an inert no-op for consumers rendered
 * outside it, where the creative-match rule never runs.
 */
export const EditedGoalIdsContext = createContext<IEditedGoalIds>({
  add: () => undefined,
  has: () => false,
});

/**
 * Observable store for {@link EditedGoalIdsContext}; `add` mutates in an action since
 * `ids` is observable. A hook, not inline, so its `this`-based methods don't trip the
 * "no `this` in function components" rule.
 */
export function useEditedGoalIds(): IEditedGoalIds {
  return useLocalObservable(() => ({
    ids: new Set<string>(),
    add(goalId: string): void {
      runInAction(() => {
        this.ids.add(goalId);
      });
    },
    has(goalId: string): boolean {
      return this.ids.has(goalId);
    },
  }));
}
